import { useConfig, useOidc } from '@revenuewell/front-end-bundle';
import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import InsuranceServiceV2, { Payer } from '../../services/insuranc-service-v2/insurance-service-v2';
import { useLocations } from '../use-locations';
import { PracticePayersContext } from './context';
import { PracticePayersContextProps } from './types';

export const PracticePayersProvider: FC<PropsWithChildren> = ({ children }) => {
  const { config } = useConfig();
  const { oidcService } = useOidc();
  const { selectedLocation } = useLocations();
  const locationId = selectedLocation?.id;

  const [payers, setPayers] = useState<Payer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [isFetched, setIsFetched] = useState(false);

  const insuranceService = useMemo(() => InsuranceServiceV2.getInstance(config, oidcService), [config, oidcService]);

  const fetchPayers = useCallback(async () => {
    if (!locationId) return;

    try {
      setIsLoading(true);
      setError(false);

      const data = await insuranceService.getPayers(locationId);
      setPayers(data);
      setIsFetched(true);
    } catch (e) {
      console.error('Failed to fetch practice payers:', e);
      setError(true);
      setPayers([]);
    } finally {
      setIsLoading(false);
    }
  }, [insuranceService, locationId]);

  useEffect(() => {
    if (!isFetched) {
      fetchPayers();
    }
  }, [isFetched, fetchPayers]);

  useEffect(() => {
    setPayers([]);
    setIsFetched(false);
  }, [locationId]);

  const getPayerById = useCallback(
    (id: string): Payer | undefined => {
      return payers.find((payer) => payer.id === id);
    },
    [payers]
  );

  const getPayerNameById = useCallback(
    (id: string): string | undefined => {
      const payer = payers.find((p) => p.id === id);
      return payer?.name;
    },
    [payers]
  );

  const refetch = useCallback(() => {
    setPayers([]);
    setIsFetched(false);
  }, []);

  const contextValue: PracticePayersContextProps = useMemo(
    () => ({
      payers,
      isLoading,
      error,
      getPayerById,
      getPayerNameById,
      refetch
    }),
    [payers, isLoading, error, getPayerById, getPayerNameById, refetch]
  );

  return <PracticePayersContext.Provider value={contextValue}>{children}</PracticePayersContext.Provider>;
};
